import React from 'react';
import { Drawer, Box, Typography, Button, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { MoreActionsMenuButton, OccupyFreeSpace, useWindowHotkey } from '../primitives';
import { FormControlsForFields } from '../schemed';
import { Schema } from '../../hooks/useSchema';
import { ExtraProps } from '../schemed/FormControlProps';
import { FormattedMessage } from 'react-intl';
import { AttributesWrapper } from './Elements/Primitives';
import { TagsListData } from './data/useTagsList';
import { TagColorPicker } from './Elements/TagColorPicker';

export interface TagEditDrawerProps {
  schema: Schema;
  editRecord: TagsListData["editRecord"];
  removeRecord: TagsListData["remove"]["run"];
}

export const TagEditDrawer = ({ schema, editRecord, removeRecord }: TagEditDrawerProps) => {
  const isDrawerOpen = !!editRecord?.item;

  const getTitle = () => {
    const r = editRecord?.item;
    if (!r) return "Редактирование";
    return r.name || "Новый тег";
  };

  const label = (f: string) => (
    <Typography key={`${f}_label`} variant="caption" color="textSecondary" className="attribute-label">
      {schema[f]?.label}
    </Typography>
  );

  const control = (f: string, options?: ExtraProps) => (
    <FormControlsForFields
      key={`${f}_control`}
      fields={[
        [f, { label: " ", ...(options || {})}],
      ]}
      schema={schema}
      data={editRecord.item}
      onChange={(o, c) => editRecord.update(c)}
    />
  );

  useWindowHotkey("esc", () => editRecord.cancel());
  useWindowHotkey("alt+s", () => editRecord.save());

  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={isDrawerOpen}
      elevation={8}
      PaperProps={{
        sx: { 
          width: 400, // Kept narrower than org/contact since there are fewer fields and no tables
          p: 2,
        }
      }}
    >
      {isDrawerOpen && (
        <Box display="flex" flexDirection="column" height="100%">
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">{getTitle()}</Typography>
            <OccupyFreeSpace />

            <MoreActionsMenuButton
              actions={[
                ["remove", <FormattedMessage id="common.delete" />, () => editRecord.item && removeRecord(editRecord.item)]
              ]}
              />
            <IconButton size="small" onClick={() => editRecord.cancel()}>
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <AttributesWrapper columns="1fr 2fr">
            {label("name")}
            {control("name", { controlProps: { autoFocus: true }})}

            {label("color")}
            <TagColorPicker
              value={editRecord.item?.color}
              update={v => editRecord.update({ color: v as any })}
              />

            {label("description")}
            {control("description", { controlProps: { multiline: true, rows: 3 }})}
          </AttributesWrapper>

          <Box mt="auto" pt={2} display="flex" justifyContent="flex-end" gap={1}>
            <Button size="small" onClick={() => editRecord.cancel()}>
              <FormattedMessage id="common.close" />
            </Button>
            {editRecord.hasChanges &&
              <Button variant="contained" color="primary" onClick={() => editRecord.save()}>
                <FormattedMessage id="common.save" />
              </Button>}
          </Box>
        </Box>
      )}
    </Drawer>
  );
};
